import React from 'react';
import { motion } from 'framer-motion';
import { ReceiptIcon, UsersIcon, UserCheckIcon, ArrowRightLeftIcon, CheckCircle2, BellIcon, WalletIcon, HistoryIcon } from 'lucide-react';
const outcomes = [{
  icon: ReceiptIcon,
  title: 'Receipt scan',
  description: 'Snap the receipt and KittyPot reads it to fill in the spend for you.'
}, {
  icon: UsersIcon,
  title: 'Share with the right people',
  description: 'Pick exactly who a spend is for, then split it equally or by custom amount.'
}, {
  icon: UserCheckIcon,
  title: 'Whoever actually paid',
  description: 'Log a spend for any member, not just yourself, so the pot always matches real life.'
}, {
  icon: ArrowRightLeftIcon,
  title: 'Who owes who',
  description: 'The fewest payments to make everyone even, with a breakdown behind each amount.'
}, {
  icon: CheckCircle2,
  title: 'Paid and confirmed',
  description: 'Mark a payment as sent by bank or cash, and the receiver confirms it landed.'
}, {
  icon: BellIcon,
  title: 'A friendly nudge',
  description: 'One tap reminds someone who still owes you, so you never have to ask twice.'
}, {
  icon: WalletIcon,
  title: 'My Balance',
  description: 'What you owe and what you are owed across every kitty, open or closed.'
}, {
  icon: HistoryIcon,
  title: 'Full history',
  description: 'Every spend and every settlement payment, kept for the whole group to see.'
}];
const alsoIncluded = ['Admin tools and join requests', 'Add people by name to claim later', 'Save bank details to share when settling', 'Push notifications', 'Closed kitties keep their balances', "Who's round is next? spin wheel", 'Your last-loaded data works offline'];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ProductDeepDive() {
  return <section id="features" className="py-24 lg:py-32 bg-gray-950 relative overflow-hidden" data-id="element-217" data-mp="28au6b82wq4sw">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-218" data-mp="2cautbh42nobk">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-kitty-yellow/5 rounded-full blur-3xl" data-id="element-219" data-mp="2hb8v4hf2y215" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-220" data-mp="2m9aobv67zhqc">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16 lg:mb-20" data-id="element-221" data-mp="1onmrj95uqxea">
          <p className="text-sm font-semibold text-kitty-yellow uppercase tracking-widest mb-4" data-id="element-222" data-mp="23bzreh8290e2">
            Features
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight" data-id="element-223" data-mp="15tpvt04mej7v">
            Everything the group needs, in one pot.
          </h2>
          <p className="text-lg text-gray-400 max-w-lg mx-auto leading-relaxed" data-id="element-224" data-mp="13avy7u0qzjyo">
            From the first round to the final settle-up.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-225" data-mp="tcf3qn2lon7v">
          {outcomes.map(item => <motion.div key={item.title} variants={itemVariants} className="rounded-2xl border border-gray-800 bg-gray-900/60 backdrop-blur-sm p-6 lg:p-8 group" data-id="element-226" data-mp="ke34jwno4h3t">
              <div className="w-12 h-12 rounded-xl bg-kitty-yellow/10 flex items-center justify-center mb-5 group-hover:bg-kitty-yellow/20 transition-colors duration-300" data-id="element-227" data-mp="alxewtkw2brs">
                <item.icon className="w-6 h-6 text-kitty-yellow" data-id="element-228" data-mp="oj9dp0i77e9" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-229" data-mp="qdlcdtsrp67">
                {item.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-230" data-mp="ldv8al826gnn">
                {item.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Secondary capabilities */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 lg:mt-16 border-t border-gray-800 pt-10" data-id="element-231" data-mp="cltf980bk2ne">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-5" data-id="element-232" data-mp="2pgmcccllstp">
            Also in every kitty
          </p>
          <ul className="flex flex-wrap gap-2.5" data-id="element-233" data-mp="3psldmqvaek9t">
            {alsoIncluded.map(item => <li key={item} className="rounded-full border border-gray-800 bg-gray-900/60 px-4 py-2 text-sm text-gray-300" data-id="element-234" data-mp="3tt597q07pftu">
                {item}
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}